// Report-a-game panel — a slide-in form shared by the main app (gameplay
// screen) and the library app (game detail + grid tiles). It is a special
// case of the feedback/contact form: it posts to the same `/api/contact`
// endpoint with `type=game_report`, auto-populates the reported game + ROM
// filename, offers a reason dropdown (default "Not working"), a description,
// and up to 4 screenshots.
//
// Self-contained on purpose: it injects its own DOM once and depends only on
// the shared DOM helper + the shared reason list, so both bundles can import
// it without pulling in app-specific auth/api modules. Auth is enforced
// server-side; a 401 response renders an inline "please sign in" state.
//
//   import { openGameReport } from '.../report-game-panel.js';
//   openGameReport({ game_id, system_id, rom_hash, rom_filename, game_title,
//                    reason?, onAuthRequired? },
//                  { returnFocus?, fallbackFocus?, onOpen?, onClose? });
//
// onOpen/onClose bracket one open panel exactly once (a reopen while it is
// still open keeps the current pair). The main app uses them for a
// gameplay-input lease so typing a description never drives the game.

import { h, on } from './dom.js';
import { GAME_REPORT_REASONS, DEFAULT_GAME_REPORT_REASON } from '../game-report-reasons.js';
import { bodyWithDiagnostics } from '../diagnostics-footer.js';
import { systemTag } from '../system-label.js';

const MAX_FILES = 4;
const MAX_FILE_SIZE = 2 * 1024 * 1024;

let els = null;          // cached DOM references once built
let selectedFiles = [];
let ctx = {};            // current game context
let escHandler = null;
let focusReturn = null;
let focusFallback = null;
let sessionOpen = false; // between an open and its close
let closeHook = null;    // the open session's onClose

function build() {
  if (els) return els;

  const title = h('h2', { class: 'report-panel-title', id: 'report-game-title' }, 'Report a problem');
  const closeBtn = h('button', {
    type: 'button', class: 'report-panel-close', 'aria-label': 'Close',
  }, '×');

  const gameLine = h('div', { class: 'report-game-name' });
  const romLine = h('div', { class: 'report-game-rom' });
  const contextBox = h('div', { class: 'report-context' },
    h('span', { class: 'report-context-label' }, 'Reporting'),
    gameLine,
    romLine,
  );

  const reasonSelect = h('select', {
    class: 'report-field-input', id: 'report-reason', name: 'reason',
  }, ...GAME_REPORT_REASONS.map(r => h('option', { value: r.value }, r.label)));

  const body = h('textarea', {
    class: 'report-field-input report-field-textarea',
    id: 'report-body', name: 'body', rows: '4',
    maxlength: '10000',
    placeholder: 'Describe the problem (optional but helpful)…',
  });

  const fileInput = h('input', {
    type: 'file', class: 'report-file-input',
    accept: 'image/jpeg,image/png,image/webp', multiple: true,
  });
  const dropzone = h('div', {
    class: 'report-dropzone', tabindex: '0', role: 'button',
    'aria-label': 'Add screenshots',
  }, h('span', {}, '📎 Add screenshots'), h('span', { class: 'report-dropzone-hint' }, 'PNG, JPG or WebP · up to 4 · 2 MB each'));
  const thumbnails = h('div', { class: 'report-thumbnails' });

  const error = h('div', { class: 'report-error', style: { display: 'none' } });
  const submit = h('button', { type: 'submit', class: 'report-submit' }, 'Submit report');

  const form = h('form', { class: 'report-form' },
    contextBox,
    h('label', { class: 'report-field-label', for: 'report-reason' }, "What's wrong?"),
    reasonSelect,
    h('label', { class: 'report-field-label', for: 'report-body' }, 'Details'),
    body,
    dropzone,
    fileInput,
    thumbnails,
    error,
    submit,
  );

  const success = h('div', { class: 'report-success', style: { display: 'none' } },
    h('div', { class: 'report-success-icon' }, '✓'),
    h('p', { class: 'report-success-title' }, 'Report received'),
    h('p', { class: 'report-success-msg' }, "Thanks, we'll take a look. You'll get a notification when it's resolved."),
    h('button', { type: 'button', class: 'report-submit', onclick: close }, 'Done'),
  );

  const content = h('div', { class: 'report-panel-content' },
    h('div', { class: 'report-panel-header' }, title, closeBtn),
    form,
    success,
  );
  const panel = h('aside', { class: 'report-panel hidden', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'report-game-title' },
    h('div', { class: 'report-panel-backdrop' }),
    content,
  );
  document.body.appendChild(panel);

  on(closeBtn, 'click', close);
  on(panel.querySelector('.report-panel-backdrop'), 'click', close);
  on(dropzone, 'click', () => fileInput.click());
  on(dropzone, 'keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.click(); } });
  on(dropzone, 'dragover', (e) => { e.preventDefault(); dropzone.classList.add('dragover'); });
  on(dropzone, 'dragleave', () => dropzone.classList.remove('dragover'));
  on(dropzone, 'drop', (e) => {
    e.preventDefault();
    dropzone.classList.remove('dragover');
    if (e.dataTransfer?.files?.length) addFiles(Array.from(e.dataTransfer.files));
  });
  on(fileInput, 'change', () => {
    if (fileInput.files?.length) addFiles(Array.from(fileInput.files));
    fileInput.value = '';
  });
  on(form, 'submit', onSubmit);

  els = { panel, content, form, success, contextBox, gameLine, romLine, reasonSelect, body, fileInput, dropzone, thumbnails, error, submit };
  return els;
}

function renderThumbnails() {
  els.thumbnails.innerHTML = '';
  selectedFiles.forEach((file, i) => {
    const img = h('img', { src: URL.createObjectURL(file), alt: file.name });
    const remove = h('button', {
      type: 'button', class: 'report-thumb-remove', 'aria-label': 'Remove',
      onclick: () => { selectedFiles.splice(i, 1); renderThumbnails(); },
    }, '×');
    els.thumbnails.appendChild(h('div', { class: 'report-thumb' }, img, remove));
  });
}

function addFiles(files) {
  const errs = [];
  for (const file of files) {
    if (selectedFiles.length >= MAX_FILES) { errs.push(`Up to ${MAX_FILES} screenshots.`); break; }
    if (file.size > MAX_FILE_SIZE) { errs.push(`${file.name} exceeds 2 MB.`); continue; }
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) { errs.push(`${file.name} is not a supported image.`); continue; }
    selectedFiles.push(file);
  }
  if (errs.length) showError(errs.join(' '));
  renderThumbnails();
}

function showError(msg) {
  els.error.textContent = msg;
  els.error.style.display = '';
}

function clearError() {
  els.error.textContent = '';
  els.error.style.display = 'none';
}

async function onSubmit(e) {
  e.preventDefault();
  clearError();
  els.submit.disabled = true;
  els.submit.textContent = 'Submitting…';

  const fd = new FormData();
  fd.append('type', 'game_report');
  fd.append('reason', els.reasonSelect.value || DEFAULT_GAME_REPORT_REASON);
  fd.append('body', bodyWithDiagnostics(els.body.value.trim()));
  if (ctx.game_id != null) fd.append('game_id', String(ctx.game_id));
  if (ctx.system_id) fd.append('system_id', ctx.system_id);
  if (ctx.rom_hash) fd.append('rom_hash', ctx.rom_hash);
  if (ctx.rom_filename) fd.append('rom_filename', ctx.rom_filename);
  if (ctx.game_title) fd.append('game_title', ctx.game_title);
  selectedFiles.forEach(f => fd.append('screenshots', f));

  try {
    const res = await fetch('/api/contact', { method: 'POST', credentials: 'include', body: fd });
    if (res.status === 401) {
      resetSubmit();
      if (typeof ctx.onAuthRequired === 'function') { close(); ctx.onAuthRequired(); return; }
      showError('Please sign in to report a game.');
      return;
    }
    if (res.status === 429) {
      resetSubmit();
      const err = await res.json().catch(() => ({}));
      showError(err.error === 'too_many_reports'
        ? "You've already reported this game recently."
        : 'Too many reports. Please try again later.');
      return;
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.ok) {
      resetSubmit();
      showError('Something went wrong. Please try again.');
      return;
    }
    els.form.style.display = 'none';
    els.success.style.display = '';
  } catch {
    resetSubmit();
    showError('Network error. Please check your connection.');
  }
}

function resetSubmit() {
  els.submit.disabled = false;
  els.submit.textContent = 'Submit report';
}

export function openGameReport(context = {}, {
  returnFocus = null, fallbackFocus = null, onOpen = null, onClose = null,
} = {}) {
  build();
  focusReturn = returnFocus || document.activeElement;
  focusFallback = fallbackFocus;
  ctx = context;
  selectedFiles = [];

  els.gameLine.textContent = context.game_title || context.rom_filename || 'this game';
  const romBits = [];
  // .report-game-rom keeps word-break: break-word so a long ROM filename can
  // wrap — never a candidate for a blanket nowrap — so only the system tag's
  // own internal space becomes non-breaking here, keeping e.g. "ATARI 2600"
  // together while the rest of the line (and a long filename) still wraps.
  if (context.system_id) romBits.push(systemTag(context.system_id).replace(' ', ' '));
  if (context.rom_filename) romBits.push(context.rom_filename);
  els.romLine.textContent = romBits.join(' · ');
  els.romLine.style.display = romBits.length ? '' : 'none';

  els.reasonSelect.value = context.reason && GAME_REPORT_REASONS.some(r => r.value === context.reason)
    ? context.reason
    : DEFAULT_GAME_REPORT_REASON;
  els.body.value = '';
  renderThumbnails();
  clearError();
  resetSubmit();
  els.form.style.display = '';
  els.success.style.display = 'none';

  // Last step before the reveal: a throwing onOpen leaves the panel hidden.
  if (!sessionOpen) {
    onOpen?.();
    sessionOpen = true;
    closeHook = onClose;
  }
  els.panel.classList.remove('hidden');
  els.panel.removeAttribute('aria-hidden');
  els.panel.removeAttribute('inert');
  els.reasonSelect.focus();
  requestAnimationFrame(() => requestAnimationFrame(() => {
    if (sessionOpen) els.panel.classList.add('open');
  }));

  escHandler = (e) => { if (e.key === 'Escape') close(); };
  document.addEventListener('keydown', escHandler);
}

function canRestoreFocus(target) {
  if (!target?.isConnected) return false;
  for (let node = target; node?.nodeType === 1; node = node.parentElement) {
    if (node.matches('[hidden], .hidden, .visually-hidden, [aria-hidden="true"], [inert]')) return false;
    const style = getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
  }
  return true;
}

export function close() {
  if (!els) return;
  els.panel.classList.remove('open');
  els.panel.setAttribute('aria-hidden', 'true');
  els.panel.setAttribute('inert', '');
  // A reopen before this slide-out finishes owns the panel (and its onClose)
  // again, so a late hide must leave it visible.
  const hide = () => { if (!sessionOpen) els.panel.classList.add('hidden'); };
  const onEnd = () => {
    els.content.removeEventListener('transitionend', onEnd);
    hide();
  };
  els.content.addEventListener('transitionend', onEnd);
  // Fallback in case transitionend doesn't fire (e.g. reduced motion).
  setTimeout(hide, 400);
  if (escHandler) { document.removeEventListener('keydown', escHandler); escHandler = null; }
  const endedHook = sessionOpen ? closeHook : null;
  sessionOpen = false;
  closeHook = null;
  endedHook?.();
  let target = canRestoreFocus(focusReturn) ? focusReturn : focusFallback?.();
  if (!canRestoreFocus(target)) {
    const menu = document.getElementById('screen-menu');
    if (canRestoreFocus(menu)) {
      target = [...menu.querySelectorAll('h2, h1')]
        .find(canRestoreFocus);
      if (target) target.tabIndex = -1;
    }
  }
  if (canRestoreFocus(target)) target.focus();
  focusReturn = null;
  focusFallback = null;
}
